import { useId } from 'react';
import { MessageSquareTextIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { NewCommentsBlockState } from '../newComments';
import type { Outcome } from '../primaryAction';

const plural = (count: number) =>
  count === 1 ? '1 new comment' : `${String(count)} new comments`;

/** Where files go, since a comment cannot carry one (spec 235 FR-004). */
const FILES_LINE = 'Files go in the task’s attachments folder.';

/**
 * The next move after commenting (spec 235): between the timeline and the compose
 * box, where the eye lands after sending. Run again on a step; on a gate, the gate's
 * own ways on, since a run there runs no agent (spec 172); while the step runs with
 * the notes, a line that says so and opens the run. The surface is the calm note's
 * (`InfoCallout`), not a warning: nothing is wrong, something is waiting.
 */
export function NewCommentsBlock({
  state,
  pendingLabel,
  onRunAgain,
  onDecide,
  onShowRun,
}: {
  state: NewCommentsBlockState;
  /** The task's own action in flight ("Starting…", "Moving…"): every control waits. */
  pendingLabel?: string;
  onRunAgain: () => void;
  onDecide: (outcome: Outcome) => void;
  onShowRun: () => void;
}) {
  const titleId = useId();
  const busy = pendingLabel !== undefined;

  if (state.kind === 'running') {
    return (
      <div
        role="status"
        className="bg-muted/40 border-border text-foreground flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-4 py-3 text-sm"
      >
        <MessageSquareTextIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-muted-foreground size-4 shrink-0"
        />
        <span className="flex-1">
          {`Running ${state.stepLabel} with ${plural(state.count)}.`}
        </span>
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0"
          onClick={onShowRun}
        >
          Show run
        </Button>
      </div>
    );
  }

  const them = state.count === 1 ? 'it' : 'them';
  return (
    <section
      aria-labelledby={titleId}
      data-new-comments-block=""
      className="bg-muted/40 border-border text-foreground flex flex-col gap-3 rounded-lg border px-4 py-3 text-sm"
    >
      <div className="flex items-start gap-3">
        <MessageSquareTextIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-muted-foreground mt-0.5 size-4 shrink-0"
        />
        <div className="flex flex-col gap-1">
          <h3 id={titleId} className="font-medium">
            {`${plural(state.count)} since the last run`}
          </h3>
          <p className="text-muted-foreground">
            {state.kind === 'run-again'
              ? `Run ${state.stepLabel} again with ${them}. The task stays on this step. ${FILES_LINE}`
              : `${state.stepLabel} waits for a decision. ${state.count === 1 ? 'The comment goes' : 'The comments go'} with whichever way on you choose. ${FILES_LINE}`}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 ps-7">
        {state.kind === 'run-again' ? (
          <Button size="sm" disabled={busy} onClick={onRunAgain}>
            {pendingLabel ?? 'Run again'}
          </Button>
        ) : (
          state.outcomes.map((outcome, index) => (
            <Button
              key={outcome.to}
              size="sm"
              variant={index === 0 ? 'default' : 'outline'}
              disabled={busy}
              onClick={() => {
                onDecide(outcome);
              }}
            >
              {busy && index === 0 ? pendingLabel : outcome.label}
            </Button>
          ))
        )}
      </div>
    </section>
  );
}
